import React, { useState } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  Paper,
  CircularProgress,
  IconButton,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  Chip,
} from "@mui/material";
import {
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
} from "@mui/icons-material";
import { api } from "../../services/api";

interface NewsSubmissionProps {
  onSuccess?: (navigateTo?: string) => void;
  // Edit mode props
  editMode?: boolean;
  newsId?: string;
  initialTitle?: string;
  initialSubtitle?: string;
  initialContent?: string;
  initialImages?: string[];
  initialVideoLinks?: string[];
}

const NewsSubmission: React.FC<NewsSubmissionProps> = ({
  onSuccess,
  editMode = false,
  newsId,
  initialTitle = "",
  initialSubtitle = "",
  initialContent = "",
  initialImages = [],
  initialVideoLinks = [],
}) => {
  const [title, setTitle] = useState(initialTitle);
  const [subtitle, setSubtitle] = useState(initialSubtitle);
  const [content, setContent] = useState(initialContent);
  const [images, setImages] = useState<string[]>(initialImages);
  const [videoLinks, setVideoLinks] = useState<string[]>(initialVideoLinks);
  const [newVideoLink, setNewVideoLink] = useState("");
  const [newImageUrl, setNewImageUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    setError(null);

    try {
      const uploadPromises = Array.from(files).map(async (file) => {
        // Validate file type
        if (!file.type.startsWith("image/")) {
          throw new Error(`${file.name} is not an image file`);
        }

        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
          throw new Error(`${file.name} exceeds 5MB size limit`);
        }

        // Convert to base64
        return new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = async () => {
            try {
              const result = await api.uploadImage({
                imageData: reader.result as string,
                fileName: file.name.replace(/\.[^/.]+$/, ""),
              });

              if (result.success && result.url) {
                resolve(result.url);
              } else {
                reject(new Error(result.error || "Upload failed"));
              }
            } catch (err) {
              reject(err);
            }
          };
          reader.onerror = () => reject(new Error(`Failed to read ${file.name}`));
          reader.readAsDataURL(file);
        });
      });

      const uploadedUrls = await Promise.all(uploadPromises);
      setImages((prev) => [...prev, ...uploadedUrls]);
    } catch (err: any) {
      setError(err.message || "Failed to upload images");
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveImage = (index: number) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleAddImageUrl = async () => {
    const trimmedUrl = newImageUrl.trim();
    if (trimmedUrl) {
      // Basic URL validation
      try {
        new URL(trimmedUrl);
      } catch (err) {
        setError("Please enter a valid image URL");
        setTimeout(() => setError(null), 3000);
        return;
      }

      setUploading(true);
      setError(null);

      try {
        // Upload URL to Cloudinary (Cloudinary fetches and stores the image)
        const result = await api.uploadImage({
          imageData: trimmedUrl,
          fileName: `url_image_${Date.now()}`,
        });

        if (result.success && result.url) {
          setImages((prev) => [...prev, result.url!]);
          setNewImageUrl("");
        } else {
          setError(result.error || "Failed to upload image from URL");
          setTimeout(() => setError(null), 3000);
        }
      } catch (err: any) {
        setError(err.message || "Failed to upload image from URL");
        setTimeout(() => setError(null), 3000);
      } finally {
        setUploading(false);
      }
    }
  };

  const handleAddVideoLink = () => {
    if (newVideoLink.trim()) {
      setVideoLinks((prev) => [...prev, newVideoLink.trim()]);
      setNewVideoLink("");
    }
  };

  const handleRemoveVideoLink = (index: number) => {
    setVideoLinks((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSaveAsDraft = async () => {
    setError(null);
    setSuccess(false);
    setLoading(true);

    try {
      let result;

      if (editMode && newsId) {
        // Edit existing news
        result = await api.editNews({
          newsId,
          title,
          subtitle,
          content,
          images: images.length > 0 ? images : undefined,
          videoLinks: videoLinks.length > 0 ? videoLinks : undefined,
        });
      } else {
        // Create new news
        result = await api.submitNews({
          title,
          subtitle,
          content,
          images: images.length > 0 ? images : undefined,
          videoLinks: videoLinks.length > 0 ? videoLinks : undefined,
        });
      }

      if (result.success) {
        setSuccess(true);
        if (!editMode) {
          // Clear form only for new submissions
          setTitle("");
          setSubtitle("");
          setContent("");
          setImages([]);
          setVideoLinks([]);
          setNewImageUrl("");
          setNewVideoLink("");
        }
        // Notify parent and navigate to drafts
        if (onSuccess) {
          console.log("Calling onSuccess with: drafts");
          onSuccess("drafts");
        }
        // Auto-hide success message after 5 seconds
        setTimeout(() => setSuccess(false), 5000);
      } else {
        setError(result.error || "Failed to save draft");
      }
    } catch (err: any) {
      setError(err.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitForReview = async () => {
    setError(null);
    setSuccess(false);
    setLoading(true);

    try {
      let finalNewsId: string | undefined;

      if (editMode && newsId) {
        // Edit existing news
        const editResult = await api.editNews({
          newsId,
          title,
          subtitle,
          content,
          images: images.length > 0 ? images : undefined,
          videoLinks: videoLinks.length > 0 ? videoLinks : undefined,
        });

        if (editResult.success) {
          // Use the editId (could be same newsId or new edit document)
          finalNewsId = editResult.editId;
        } else {
          setError(editResult.error || "Failed to update news");
          setLoading(false);
          return;
        }
      } else {
        // Create new news as draft
        const result = await api.submitNews({
          title,
          subtitle,
          content,
          images: images.length > 0 ? images : undefined,
          videoLinks: videoLinks.length > 0 ? videoLinks : undefined,
        });

        if (result.success && result.newsId) {
          finalNewsId = result.newsId;
        } else {
          setError(result.error || "Failed to create news");
          setLoading(false);
          return;
        }
      }

      // Update status to in-review
      if (finalNewsId) {
        const updateResult = await api.updateNews({
          newsId: finalNewsId,
          status: "in-review",
        });

        if (updateResult.success) {
          setSuccess(true);
          if (!editMode) {
            // Clear form only for new submissions
            setTitle("");
            setSubtitle("");
            setContent("");
            setImages([]);
            setVideoLinks([]);
            setNewImageUrl("");
            setNewVideoLink("");
          }
          // Notify parent and navigate to in-review
          if (onSuccess) {
            console.log("Calling onSuccess with: in-review");
            onSuccess("in-review");
          }
          // Auto-hide success message after 5 seconds
          setTimeout(() => setSuccess(false), 5000);
        } else {
          setError(updateResult.error || "Failed to submit for review");
        }
      }
    } catch (err: any) {
      setError(err.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Default behavior is to save as draft
    await handleSaveAsDraft();
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        {editMode ? "Edit News Article" : "Report News"}
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        {editMode ? "Update the news article details" : "Report a news article for review"}
      </Typography>

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {editMode ? "News article updated successfully!" : "News article saved successfully!"}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper elevation={2} sx={{ p: 3, mt: 2 }}>
        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            margin="normal"
            placeholder="Enter news title"
          />

          <TextField
            fullWidth
            label="Subtitle"
            value={subtitle}
            onChange={(e) => setSubtitle(e.target.value)}
            required
            margin="normal"
            placeholder="Enter news subtitle"
          />

          <TextField
            fullWidth
            label="Content"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            required
            multiline
            rows={10}
            margin="normal"
            placeholder="Write your news article content here..."
          />

          {/* Image Upload Section */}
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              Images
            </Typography>
            <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap", alignItems: "flex-start" }}>
              <Button
                variant="outlined"
                component="label"
                startIcon={uploading ? <CircularProgress size={20} /> : <UploadIcon />}
                disabled={uploading || loading}
              >
                {uploading ? "Uploading..." : "Upload Images"}
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  multiple
                  onChange={handleImageUpload}
                />
              </Button>
              <Typography variant="caption" color="text.secondary" sx={{ alignSelf: "center" }}>
                Max 5MB per image. Supported: JPG, PNG, GIF, WebP
              </Typography>
            </Box>

            {/* Add Image URL */}
            <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
              <TextField
                fullWidth
                size="small"
                label="Or paste Image URL"
                value={newImageUrl}
                onChange={(e) => setNewImageUrl(e.target.value)}
                placeholder="https://example.com/image.jpg"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleAddImageUrl();
                  }
                }}
              />
              <Button
                variant="outlined"
                startIcon={uploading ? <CircularProgress size={20} /> : <AddIcon />}
                onClick={handleAddImageUrl}
                disabled={!newImageUrl.trim() || loading || uploading}
              >
                {uploading ? "Uploading..." : "Add"}
              </Button>
            </Box>

            {images.length > 0 && (
              <ImageList sx={{ mt: 2 }} cols={3} gap={8}>
                {images.map((url, index) => (
                  <ImageListItem key={index}>
                    <img
                      src={url}
                      alt={`Upload ${index + 1}`}
                      loading="lazy"
                      style={{ height: 200, objectFit: "cover" }}
                    />
                    <ImageListItemBar
                      actionIcon={
                        <IconButton
                          sx={{ color: "rgba(255, 255, 255, 0.9)" }}
                          onClick={() => handleRemoveImage(index)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      }
                    />
                  </ImageListItem>
                ))}
              </ImageList>
            )}
          </Box>

          {/* Video Links Section */}
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              Video Links (Optional)
            </Typography>
            <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
              <TextField
                fullWidth
                size="small"
                label="Video URL"
                value={newVideoLink}
                onChange={(e) => setNewVideoLink(e.target.value)}
                placeholder="https://youtube.com/watch?v=..."
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleAddVideoLink();
                  }
                }}
              />
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={handleAddVideoLink}
                disabled={!newVideoLink.trim()}
              >
                Add
              </Button>
            </Box>

            {videoLinks.length > 0 && (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {videoLinks.map((link, index) => (
                  <Chip
                    key={index}
                    label={link}
                    onDelete={() => handleRemoveVideoLink(index)}
                    color="primary"
                    variant="outlined"
                  />
                ))}
              </Box>
            )}
          </Box>

          <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
            <Button
              type="submit"
              variant="outlined"
              color="primary"
              disabled={loading || uploading}
              startIcon={loading ? <CircularProgress size={20} /> : null}
            >
              {loading ? (editMode ? "Updating..." : "Saving...") : (editMode ? "Update as Draft" : "Save as Draft")}
            </Button>
            <Button
              type="button"
              variant="contained"
              color="primary"
              onClick={handleSubmitForReview}
              disabled={loading || uploading}
              startIcon={loading ? <CircularProgress size={20} /> : null}
            >
              {loading ? (editMode ? "Updating..." : "Submitting...") : (editMode ? "Update & Submit" : "Submit")}
            </Button>
            <Button
              type="button"
              variant="outlined"
              onClick={() => {
                setTitle("");
                setSubtitle("");
                setContent("");
                setImages([]);
                setVideoLinks([]);
                setNewImageUrl("");
                setNewVideoLink("");
                setError(null);
                setSuccess(false);
              }}
              disabled={loading || uploading}
            >
              Clear
            </Button>
          </Box>
        </form>
      </Paper>
    </Box>
  );
};

export default NewsSubmission;
